import { useCallback, useEffect, useState } from 'react'
import { secureFetch, secureJsonFetch } from '@/utils/SecureFetch'
import { Check, Link as LinkIcon, Mail, Trash2 } from 'lucide-react'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/Dialog'
import { PermissionsSelector } from './PermissionsSelector'
import { Button } from '../ui/Button'
import { ButtonTabs } from '../ui/ButtonTabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select'
import { useTeamsData } from '@/hooks/UseTeamsData'
import './playbook-share-dialog.css'

type PlaybookShareDialogProps = {
	isOpen: boolean
	onClose: () => void
	playbookId: string
	playbookName: string
	currentTeamId?: string | null
}

type TeamShare = {
	id: string
	playbook_id: string
	shared_with_team_id: string
	permission: 'view' | 'edit'
	team_name: string
}

type EmailRecipient = {
	email: string
	permission: 'view' | 'edit'
}

const LINK_COPIED_TIMEOUT_MS = 2000

const TAB_OPTIONS = [
	{ value: 'link' as const, label: 'Link' },
	{ value: 'teams' as const, label: 'Teams' },
	{ value: 'people' as const, label: 'Email' },
]

export function PlaybookShareDialog({
										isOpen,
										onClose,
										playbookId,
										playbookName,
										currentTeamId,
									}: PlaybookShareDialogProps) {
	// Link tab state
	const [linkCopied, setLinkCopied] = useState(false)
	const [activeTab, setActiveTab] = useState<'link' | 'teams' | 'people'>('link')
	const [linkPermission, setLinkPermission] = useState<'view' | 'edit'>('view')

	// Team tab state
	const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null)
	const [teamPermission, setTeamPermission] = useState<'view' | 'edit'>('view')
	const [teamShares, setTeamShares] = useState<TeamShare[]>([])
	const [isLoadingTeams, setIsLoadingTeams] = useState(false)
	const [isSharingTeam, setIsSharingTeam] = useState(false)
	const { teams } = useTeamsData()

	// People tab state
	const [emailInput, setEmailInput] = useState('')
	const [emailRecipients, setEmailRecipients] = useState<EmailRecipient[]>([])
	const [emailPermission, setEmailPermission] = useState<'view' | 'edit'>('view')
	const [isSharingEmail, setIsSharingEmail] = useState(false)

	const availableTeams = teams.filter(team => team.id !== currentTeamId)

	const loadTeamShares = useCallback(async () => {
		setIsLoadingTeams(true)
		try {
			const response = await secureFetch(`/api/playbooks/${playbookId}/shares`)
			if (response.ok) {
				const data = await response.json()
				setTeamShares(data.shares || [])
			}
		} catch (err) {
			console.error('Error loading team shares:', err)
		} finally {
			setIsLoadingTeams(false)
		}
	}, [playbookId])

	// Load team shares when dialog opens
	useEffect(() => {
		if (isOpen) {
			loadTeamShares()
			setSelectedTeamId(availableTeams[0]?.id ?? null)
		}
	}, [isOpen, playbookId, loadTeamShares, availableTeams])

	// Link handlers
	function handleCopyLink() {
		const origin = window.location.origin
		const shareLink = `${origin}/shared/playbook/${playbookId}?permission=${linkPermission}`
		navigator.clipboard.writeText(shareLink)
		setLinkCopied(true)
		setTimeout(() => setLinkCopied(false), LINK_COPIED_TIMEOUT_MS)
	}

	// Team handlers
	async function handleShareWithTeam() {
		if (!selectedTeamId) return
		setIsSharingTeam(true)
		try {
			const response = await secureJsonFetch(`/api/playbooks/${playbookId}/shares`, {
				method: 'POST',
				body: { team_id: selectedTeamId, permission: teamPermission },
			})
			if (response.ok) {
				await loadTeamShares()
				setSelectedTeamId(availableTeams[0]?.id ?? null)
				setTeamPermission('view')
			}
		} catch (err) {
			console.error('Error sharing with team:', err)
		} finally {
			setIsSharingTeam(false)
		}
	}

	async function handleRemoveTeamShare(teamId: string) {
		try {
			const response = await secureFetch(`/api/playbooks/${playbookId}/shares/${teamId}`, {
				method: 'DELETE',
			})
			if (response.ok) {
				await loadTeamShares()
			}
		} catch (err) {
			console.error('Error removing team share:', err)
		}
	}

	// Email validation helper
	function isValidEmail(email: string): boolean {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
		return emailRegex.test(email)
	}

	// Email handlers
	function handleAddEmail() {
		const trimmedEmail = emailInput.trim()
		const emailExists = emailRecipients.some(r => r.email === trimmedEmail)
		if (trimmedEmail && isValidEmail(trimmedEmail) && !emailExists) {
			setEmailRecipients([...emailRecipients, { email: trimmedEmail, permission: emailPermission }])
			setEmailInput('')
		}
	}

	function handleRemoveEmail(email: string) {
		setEmailRecipients(emailRecipients.filter(r => r.email !== email))
	}

	async function handleShareWithPeople() {
		if (emailRecipients.length === 0) return
		setIsSharingEmail(true)
		try {
			// API call to share with email recipients
			// For now, just clear the list
			setEmailRecipients([])
			setEmailInput('')
		} finally {
			setIsSharingEmail(false)
		}
	}

	const canAddEmail = emailInput.trim() && emailInput.includes('@')

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="playbook-share-dialog">
				<DialogHeader>
					<DialogTitle>Share "{playbookName}"</DialogTitle>
				</DialogHeader>

				<div className="playbook-share-content">
					<ButtonTabs
						options={TAB_OPTIONS}
						value={activeTab}
						onChange={setActiveTab}
						stretch
						aria-label="Share method"
					/>

					<div className="playbook-share-tab-content">
						{activeTab === 'link' && (
							<div className="playbook-share-link-section">
								<PermissionsSelector
									value={linkPermission}
									onChange={setLinkPermission}
								/>
								<div className="playbook-share-link-card">
									<div className="playbook-share-link-info">
										<div className="playbook-share-link-icon">
											<LinkIcon className="size-5" />
										</div>
										<div className="playbook-share-link-text">
											<p className="playbook-share-link-title">Share via link</p>
										</div>
									</div>
									<Button onClick={handleCopyLink} variant="action">
										{linkCopied ? (
											<><Check className="size-4" /> Copied</>
										) : (
											'Copy Link'
										)}
									</Button>
								</div>
							</div>
						)}

						{activeTab === 'teams' && (
							<div className="playbook-share-team-form">
								{availableTeams.length === 0 ? (
									<p className="playbook-share-list-empty">
										No other teams available to share with.
									</p>
								) : (
									<>
										<PermissionsSelector
											value={teamPermission}
											onChange={setTeamPermission}
											disabled={isSharingTeam}
										/>

										<div>
											<label className="playbook-share-label">Share with team</label>
											<Select
												value={selectedTeamId?.toString() || ''}
												onValueChange={(value) => setSelectedTeamId(value)}
												disabled={isSharingTeam}
											>
												<SelectTrigger className="playbook-share-team-select">
													<SelectValue placeholder="Select a team" />
												</SelectTrigger>
												<SelectContent>
													{availableTeams.map(team => (
														<SelectItem key={team.id} value={team.id.toString()}>
															{team.name}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>

										<Button
											variant="action"
											onClick={handleShareWithTeam}
											disabled={isSharingTeam || !selectedTeamId}
											className="playbook-share-full-width"
										>
											{isSharingTeam ? 'Sharing...' : 'Share with Team'}
										</Button>
									</>
								)}

								{/* Current team shares */}
								<div>
									<h3 className="playbook-share-list-header">
										Shared with {teamShares.length} team{teamShares.length !== 1 ? 's' : ''}
									</h3>
									{isLoadingTeams ? (
										<p className="playbook-share-list-empty">Loading...</p>
									) : teamShares.length === 0 ? (
										<p className="playbook-share-list-empty">
											Not shared with any teams yet.
										</p>
									) : (
										<div className="playbook-share-list">
											{teamShares.map(share => (
												<div key={share.id} className="playbook-share-item">
													<div className="playbook-share-item-info">
														<p className="playbook-share-item-name">{share.team_name}</p>
														<p className="playbook-share-item-detail">
															{share.permission} access
														</p>
													</div>
													<Button
														variant="ghost"
														size="sm"
														onClick={() => handleRemoveTeamShare(share.shared_with_team_id)}
														className="playbook-share-delete-button"
													>
														<Trash2 className="size-4" />
													</Button>
												</div>
											))}
										</div>
									)}
								</div>
							</div>
						)}

						{activeTab === 'people' && (
							<div className="playbook-share-email-form">
								<PermissionsSelector
									value={emailPermission}
									onChange={setEmailPermission}
								/>
								<div>
									<label className="playbook-share-label">Share with people</label>
									<div className="playbook-share-email-row">
										<div className="playbook-share-email-input-wrapper">
											<Mail className="playbook-share-email-input-icon" />
											<input
												type="email"
												value={emailInput}
												onChange={(e) => setEmailInput(e.target.value)}
												onKeyDown={(e) => {
													if (e.key === 'Enter') {
														e.preventDefault()
														handleAddEmail()
													}
												}}
												placeholder="Enter email address..."
												className="playbook-share-email-input"
											/>
										</div>
										<Button
											onClick={handleAddEmail}
											disabled={!canAddEmail}
											variant="secondary"
										>
											Add
										</Button>
									</div>
								</div>

								{emailRecipients.length > 0 && (
									<>
										<div className="playbook-share-list">
											{emailRecipients.map(recipient => (
												<div key={recipient.email} className="playbook-share-item">
													<div className="playbook-share-recipient-info">
														<div className="playbook-share-recipient-avatar">
															{recipient.email?.[0]?.toUpperCase() ?? '?'}
														</div>
														<div>
															<p className="playbook-share-item-name">{recipient.email}</p>
															<p className="playbook-share-item-detail">{recipient.permission}</p>
														</div>
													</div>
													<Button
														variant="ghost"
														size="sm"
														onClick={() => handleRemoveEmail(recipient.email)}
														className="playbook-share-delete-button"
													>
														<Trash2 className="size-4" />
													</Button>
												</div>
											))}
										</div>

										<Button
											variant="action"
											onClick={handleShareWithPeople}
											disabled={isSharingEmail || emailRecipients.length === 0}
											className="playbook-share-full-width"
										>
											{isSharingEmail ? 'Sharing...' : `Share with ${emailRecipients.length} ${emailRecipients.length === 1 ? 'person' : 'people'}`}
										</Button>
									</>
								)}

								<p className="playbook-share-list-empty">
									Recipients will receive an email with a link to view this playbook.
								</p>
							</div>
						)}
					</div>
				</div>

				<DialogFooter>
					<Button onClick={onClose} variant="outline">
						Close
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
